// src/components/roles/AddRole/index.tsx
import React, { useEffect, useState } from "react";
import DrawerLayout from "../..";
import { ProfileIcon } from "../../../../icons";
import Input from "../../../form/input";
import Switch from "../../../switch";
import PermissionDrawer from "../SetPermission";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSaveRole } from "../../../../hooks/useSaveRole";
import { message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { isPermit } from "../../../../utils/permissionCheck";

interface AddRoleProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: string;
  user?: any;
  mode: "add" | "edit" | "view";
  onEditClick?: () => void;
  onRemoveRole?: () => void;
}

export type RoleFormData = {
  role_name: string;
  role_description: string;
  active: boolean;
  permissions?: Record<number, number[]>;
};

const AddRole: React.FC<AddRoleProps> = ({
  open,
  setOpen,
  title = "Add Role",
  user,
  mode,
  onEditClick,
  onRemoveRole,
}) => {
  const canEditRole = isPermit(5, "edit");

  const queryClient = useQueryClient();

  const { data: permissionsData } = usePermissions();
  const saveRoleMutation = useSaveRole();
  const [messageApi, contextHolder] = message.useMessage();

  const [formData, setFormData] = useState<RoleFormData>({
    role_name: "",
    role_description: "",
    active: true,
    permissions: {},
  });
  const [hasOpenedPermDrawer, setHasOpenedPermDrawer] = useState(false);

  const [permDrawerOpen, setPermDrawerOpen] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // Flatten nested permissions for Tree component
  const flattenPermissions = (perms?: Record<number, number[]>) => {
    if (!perms) return [];
    return Object.entries(perms).flatMap(([moduleId, permIds]) =>
      permIds?.map((permId) => `module-${moduleId}-perm-${permId}`)
    );
  };

  // helper to normalize API permissions to nested { module_id: [permIds] }
  const normalizePermissions = (
    perms: { module_id: number; permission_id: number }[]
  ): Record<number, number[]> => {
    const grouped: Record<number, number[]> = {};
    perms?.forEach((p) => {
      if (!grouped[p.module_id]) grouped[p.module_id] = [];
      grouped[p.module_id].push(p.permission_id);
    });
    return grouped;
  };

  useEffect(() => {
    if (mode === "edit" && user && !hasOpenedPermDrawer) {
      const nestedPerms = normalizePermissions(user.permissions || []);
      setFormData({
        role_name: user.role_name,
        role_description: user.role_description,
        active: user.role_status_flg === 1 || user.role_status_flg === "1",
        permissions: nestedPerms,
      });
      setSelectedPermissions(flattenPermissions(nestedPerms));
    } else if (mode === "add" && !hasOpenedPermDrawer) {
      setFormData({
        role_name: "",
        role_description: "",
        active: true,
        permissions: {},
      });
      setSelectedPermissions([]);
    }
  }, [mode, user, hasOpenedPermDrawer, open]);

  // Convert flat permissions to API payload
  const nestPermissions = (
    flatPerms: string[]
  ): { module_id: number; permission_id: number }[] => {
    return flatPerms
      .map((p) => {
        const match = p.match(/module-(\d+)-perm-(\d+)/);
        if (!match) return null;
        return {
          module_id: parseInt(match[1], 10),
          permission_id: parseInt(match[2], 10),
        };
      })
      .filter(Boolean) as { module_id: number; permission_id: number }[];
  };

  // Save role + permissions to backend
  const handleSave = () => {
    const payload: any = {
      action: mode === "add" ? "create" : "update",
      role_name: formData.role_name,
      role_description: formData.role_description,
      role_status_flg: formData.active ? 1 : 0,
      permissions: nestPermissions(selectedPermissions),
    };
    if (mode === "edit" && user?.role_id)
      payload.role_id = Number(user.role_id);

    saveRoleMutation.mutate(payload, {
      onSuccess: (res) => {
        messageApi.success(
          mode === "add"
            ? "Role created successfully "
            : "Role updated successfully "
        );
        setPermDrawerOpen(false),
          setOpen(false),
          queryClient.invalidateQueries({ queryKey: ["roles"], exact: false });
      },
      onError: (err: any) => {
        // Check if it's an AxiosError with a response
        if (err?.response?.data?.message) {
          messageApi.error(err.response.data.message);
        } else {
          messageApi.error(err?.message || "Failed to save role");
        }
      },
    });
  };
  const handleConfirmClick = () => {
    if (mode === "view") {
      onEditClick?.();
      return;
    }

    // Validate required fields
    if (!formData.role_name.trim()) {
      messageApi.warning("Role name is required");
      return;
    }
    if (!formData.role_description.trim()) {
      messageApi.warning("Description is required");
      return;
    }

    setHasOpenedPermDrawer(true); // mark that user entered permissions step
    setPermDrawerOpen(true);
    // setOpen(false);
  };

  const getConfirmText = () => {
    if (mode === "view") return "Edit Role";
    if (mode === "add") return "Set permission";
    if (mode === "edit") return "Edit permission";
    return "Confirm";
  };

  const labelMappings = [
    { label: "Name", value: user?.role_name },
    { label: "Description", value: user?.role_description },
    { label: "Number of users", value: `${user?.no_of_users || "0"} users` },
    { label: "Created on", value: user?.created_date },
  ];

  const StatusPill: React.FC<{ status: number }> = ({ status }) => {
    const label =
      status === 1 ? "Active" : status === 0 ? "Inactive" : "Deleted";
    return (
      <span className={`status-pill status-${label.toLowerCase()}`}>
        {label}
      </span>
    );
  };

  return (
    <>
      {contextHolder}
      <DrawerLayout
        extra={<ProfileIcon />}
        open={open}
        canEditRole={mode === "view" && canEditRole} // ✅ hide bottom link
        setOpen={setOpen}
        loading={saveRoleMutation.isPending}
        title={title}
        confirmText={
          mode === "view" && !canEditRole ? undefined : getConfirmText()
        } // ✅ hide
        cancelText={mode === "view" ? "view permissions" : "Cancel"}
        onConfirm={
          mode === "view" && !canEditRole ? undefined : handleConfirmClick
        } // ✅ hide click
        onCancel={() => {
          if (mode === "view") {
            const nestedPerms = normalizePermissions(user?.permissions || []);
            const flatPerms = flattenPermissions(nestedPerms);
            setSelectedPermissions(flatPerms);
            setOpen(false);
            setPermDrawerOpen(true);
          } else {
            setOpen(false);
          }
        }}
        onRemoveRole={isPermit(5, "delete") ? onRemoveRole : undefined} // ✅ hide remove
        footerLink={isPermit(5, "delete")} // ✅ hide bottom link
      >
        {mode === "view" ? (
          <>
            <div className="details-view-wrapper">
              <>
                <div className="details-view  flex flex-col gap-4 mb-0">
                  {labelMappings.map((item, index) => (
                    <p className="label flex flex-col gap-1" key={index}>
                      {item.label}{" "}
                      <span className="value">{item.value || "-"}</span>
                    </p>
                  ))}
                </div>

                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1 status-wrapper mt-0">
                    <span className="label mb-0">Status</span>
                    <StatusPill status={user?.role_status_flg ?? -1} />
                  </div>
                </div>
              </>
            </div>

            {/* <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1 status-wrapper">
                <span className="label mb-0">Status</span>
                <StatusPill status={user?.profile_status_flg ?? -1} />
              </div>
              <div className="flex flex-col roll-wrapper">
                <span>Role</span>
                <span>{user?.role_name}</span>
              </div>
            </div> */}
          </>
        ) : (
          <div className="flex flex-col gap-6">
            <Input
              label="Role Name"
              placeholder="Enter role name"
              value={formData.role_name}
              onChange={(e) =>
                setFormData({ ...formData, role_name: e.target.value })
              }
            />
            <div>
              <label className="font-medium">Description</label>
              <textarea
                placeholder="Enter a description"
                className="text-area w-full"
                rows={4}
                value={formData.role_description}
                onChange={(e) =>
                  setFormData({ ...formData, role_description: e.target.value })
                }
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="font-medium text-gray-700">Active</label>
              <Switch
                size="default"
                checked={formData.active}
                onChange={(checked) =>
                  setFormData({ ...formData, active: checked })
                }
              />
            </div>
          </div>
        )}
      </DrawerLayout>

      <PermissionDrawer
        loading={saveRoleMutation.isPending}
        open={permDrawerOpen}
        setOpen={setPermDrawerOpen}
        permissions={selectedPermissions}
        setPermissions={setSelectedPermissions}
        permissionsData={permissionsData}
        onSave={(newPerms) => {
          setSelectedPermissions(newPerms);
          handleSave();
        }}
        onBack={() => {
          setPermDrawerOpen(false);
          setOpen(true);
        }}
        viewMode={mode === "view"}
      />
    </>
  );
};

export default AddRole;
